/** Figma "Roles & permissions": what each role can do, read from the permissions Identity grants. */
import { Check, Minus } from "lucide-react";

import type { PermissionInfo, Role } from "@/lib/types";

import { customerRoles, describeRole, matrixGroups, SHORT_NAME } from "./roles";

export function RolesMatrix({
  roles,
  permissions,
  myRoleKeys = [],
}: {
  roles: Role[];
  permissions: PermissionInfo[];
  myRoleKeys?: string[];
}) {
  const columns = customerRoles(roles);
  const groups = matrixGroups(roles, permissions);
  const mine = new Set(myRoleKeys);
  const holds = (r: Role, p: string) => r.permissions.includes("*") || r.permissions.includes(p);
  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold text-fg">Compare roles</h2>
      <div className="overflow-x-auto rounded-[14px] border border-border bg-surface">
        <table className="w-full min-w-[880px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-border bg-brand-50/60 text-xs text-fg-subtle">
              <th scope="col" className="px-4 py-2.5 text-left font-medium">
                <span className="sr-only">Capability</span>
              </th>
              {columns.map((r) => (
                <th
                  key={r.id}
                  scope="col"
                  title={describeRole(r)}
                  className="px-2 py-2.5 text-center font-medium"
                >
                  <span className="block">{SHORT_NAME[r.key] ?? r.name}</span>
                  {mine.has(r.key) && (
                    <span className="mt-1 block text-[10px] font-semibold text-brand-600">You</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <GroupRows key={group.area} group={group} columns={columns} holds={holds} />
            ))}
          </tbody>
        </table>
        <p className="border-t border-border bg-brand-50/60 px-4 py-2.5 text-xs text-fg-subtle">
          ✓ Included · These capabilities come from the live Identity permission catalogue. A role covers the
          whole business unless it's narrowed to specific websites or domain folders from Manage.
        </p>
      </div>
    </div>
  );
}

function GroupRows({
  group,
  columns,
  holds,
}: {
  group: {
    area: string;
    label: string;
    capabilities: { permission: string; label: string; description: string }[];
  };
  columns: Role[];
  holds: (role: Role, permission: string) => boolean;
}) {
  return (
    <>
      <tr className="border-t border-border bg-brand-50/40">
        <th
          scope="colgroup"
          colSpan={columns.length + 1}
          className="px-4 py-2 text-left text-xs font-bold text-fg-subtle uppercase"
        >
          {group.label}
        </th>
      </tr>
      {group.capabilities.map((capability) => (
        <tr key={capability.permission} className="border-t border-border">
          <th
            scope="row"
            className="px-4 py-2.5 text-left font-normal text-fg"
            title={capability.description}
          >
            <span className="block">{capability.label}</span>
            <span className="mt-0.5 block text-xs text-fg-subtle">{capability.description}</span>
          </th>
          {columns.map((role) => (
            <td key={role.id} className="px-2 py-2.5 text-center">
              {holds(role, capability.permission) ? (
                <Check className="mx-auto size-4 text-[#13734d]" aria-label="Included" />
              ) : (
                <Minus className="mx-auto size-4 text-[#d6cfcb]" aria-label="Not included" />
              )}
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
